import { GetApiBorrowersGetGlossaryResponse, LoanType } from "@cxm/client";
import { loanTypes } from "@cxm/lib";
import * as d3 from "d3";
import React from "react";

type WheelProps = {
  onSelectType: (type: string) => void;
  selectedType?: LoanType;
  onHoverType: (type: string | undefined) => void;
  slices: WheelStateEntry[];
  size?: number;
  loanTypeDescriptions: { [key: string]: React.ReactNode }
};

type WheelStateEntry = {
  name: string;
  color: string;
  id: LoanType;
  disabled?: boolean;
};

class Wheel extends React.Component<WheelProps, {}> {
  loanTypeDescriptions: { [key: string]: React.ReactNode } = {};
  state = {
    wheelNode: undefined as
      | d3.Selection<SVGGElement, unknown, null, undefined>
      | undefined,
    hoveredId: undefined as LoanType | undefined,
    rotation: 0,
  };

  selectedId = 0;

  maxWheelSize = 600;

  wheelSize = this.props.size ?? 400;

  mouseEnabled = false;

  colorThreshold = 0.3;
  constructor(props: WheelProps) {
    super(props);
    if (props.size) {
      this.wheelSize = props.size;
    }
  }
  componentDidMount(): void {
    this.renderWheel();
    d3.select(`.wheel-injection-point #arc-${this.props.selectedType}`)
      .transition()
      .attr(
        "transform",
        function(d: d3.PieArcDatum<WheelStateEntry>) {
          let dist = 4;
          var midAngle = (d.endAngle - d.startAngle) / 2 + d.startAngle;
          let x = Math.sin(midAngle) * dist;
          let y = -Math.cos(midAngle) * dist;
          return "translate(" + x + "," + y + ") scale(1.08)";
        } as any,
      );
    if (this.props.selectedType != undefined) {
      this.spinToSliceById(this.props.selectedType);
    }
  }

  componentWillUnmount(): void {
    if (this.state.wheelNode) {
      this.state.wheelNode.selectAll("*").remove();
    }
  }

  transRotate(rot: number, trans: string) {
    // return USE_LEGACY_SAFARI_OPTIONS
    // `translate(${trans}), rotate(${rot})`
    // return `rotate(${rot}), translate(${trans})`;
    return `rotate(${rot}), translate(${trans})`;
  }

  spinToSliceById(sliceId: LoanType) {
    this.mouseEnabled = false;
    const wheel = this;
    d3.select("svg g")
      .attr("transform-origin", "center")
      .attr("key", "ONBOARDWHEEL")
      .transition()
      .duration(800)
      .ease(d3.easeBackInOut)
      .attrTween("transform", function(d, i) {
        let sliceDeg = 430 -
          (360 / wheel.props.slices.length *
            wheel.props.slices.findIndex((x) => x.id === sliceId));
        if (wheel.state.rotation === sliceDeg) {
          let r = d3.interpolate(
            sliceDeg || 70,
            sliceDeg ? sliceDeg + 360 : 430,
          );
          let xy = wheel.wheelSize / 2;
          return (t) => wheel.transRotate(r(t), `${xy}, ${xy}`);
        } else {
          let r = d3.interpolate(wheel.state.rotation || 70, sliceDeg);
          wheel.state.rotation = sliceDeg;
          let xy = wheel.wheelSize / 2;
          return (t) => wheel.transRotate(r(t), `${xy}, ${xy}`);
          //return function (t) {
          //    let rot = `rotate(${r(t)})`
          //    let trans = `translate(${xy}, ${xy})`
          //    return IS_SAFARI ? `${trans}, ${rot}` : `${rot}, ${trans}`;
          //};
        }
      }).on("end", () => {
        this.mouseEnabled = true;
        this.updateSliceColors();
      });
  }

  updateSliceColors() {
    const wheel = this;
    this.props.slices.forEach(function(slice) {
      // Otherwise, color it with its normal color
      wheel.colorSliceById(slice.id);
    });
  }

  colorSliceById(id: LoanType) {
    d3.selectAll(".arc path").filter((s: any) => s.data.id === id).each(
      function(s: any) {
        if (!s.data.disabled) {
          d3.select(this).style("fill", s.data.color);
        } else {
          d3.select(this).style("fill", "#999");
        }
      },
    );
  }

  componentDidUpdate(
    prevProps: Readonly<WheelProps>,
    prevState: Readonly<{}>,
    snapshot?: any,
  ): void {
    // Check if slices have changed
    if (JSON.stringify(prevProps.slices) !== JSON.stringify(this.props.slices)) {
      // Clear existing wheel
      if (this.state.wheelNode) {
        this.state.wheelNode.selectAll("*").remove();
      }
      // Re-render wheel with new slices
      this.renderWheel();
    }

    if (this.props.selectedType != prevProps.selectedType) {
      if (this.props.selectedType != undefined) {
        this.spinToSliceById(this.props.selectedType);
      }
      d3.select(`.wheel-injection-point #arc-${prevProps.selectedType}`)
        .transition()
        .attr(
          "transform",
          function(d: d3.PieArcDatum<WheelStateEntry>) {
            let dist = 4;
            var midAngle = (d.endAngle - d.startAngle) / 2 + d.startAngle;
            let x = Math.sin(midAngle) * dist;
            let y = -Math.cos(midAngle) * dist;
            return "translate(0,0) scale(1)";
          } as any,
        );
      d3.select(`.wheel-injection-point #arc-${this.props.selectedType}`)
        .transition()
        .attr(
          "transform",
          function(d: d3.PieArcDatum<WheelStateEntry>) {
            let dist = 4;
            var midAngle = (d.endAngle - d.startAngle) / 2 + d.startAngle;
            let x = Math.sin(midAngle) * dist;
            let y = -Math.cos(midAngle) * dist;
            return "translate(" + x + "," + y + ") scale(1.08)";
          } as any,
        );
    }
  }

  modHexLuminosity(hex: string, lum: number) {
    // Validate hex string
    hex = String(hex).replace(/[^0-9a-f]/gi, "");
    if (hex.length < 6) {
      var firstHex = hex[0] || "0";
      var secondHex = hex[1] || "0";
      var thirdHex = hex[2] || "0";
      hex = firstHex + firstHex + secondHex + secondHex + thirdHex + thirdHex;
    }
    lum = lum || 0;

    // Convert to decimal and change luminosity
    let newHex = "#";
    for (let i = 0; i < 3; i++) {
      let c = parseInt(hex.substr(i * 2, 2), 16);
      let cstr = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(
        16,
      );
      newHex += ("00" + c).substr(cstr.length);
    }

    return newHex;
  }

  renderWheel() {
    var div = d3.select(".wheel-injection-point").node();
    if (!div) return;

    // Clear any existing SVG elements
    d3.select(".wheel-injection-point").selectAll("svg").remove();

    let wheel = this;
    const width = this.wheelSize;
    const height = this.wheelSize;

    const data = wheel.props.slices;

    let d3div = d3
      .select(div)
      .append("svg")
      .attr("class", "d3")
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform-origin", "center")
      .attr("transform", "translate(" + width / 2 + ", " + height / 2 + ")")
      .style("z-index", "10");

    this.setState({
      ...this.state,
      wheelNode: d3div,
    });

    // Draw base circle
    d3.select(".wheel-injection-point svg")
      .insert("circle", ":first-child")
      .style("fill", "white")
      .attr("cx", "50%")
      .attr("cy", "50%")
      .attr("r", 0)
      .transition()
      .delay(750)
      .duration(500)
      .attr("r", width * 0.42);

    const outerRadius = width / 2.4;
    const innerRadius = width / 20;

    const arc = d3.arc<d3.PieArcDatum<WheelStateEntry>>().outerRadius(
      outerRadius,
    ).innerRadius(
      innerRadius,
    );

    const pie = d3.pie<WheelStateEntry>().value((d) => {
      // use to be 'weight' per slice
      return 1;
    });

    const g = d3div
      .selectAll(".arc")
      .data(pie(data))
      .enter()
      .append("g")
      .attr("class", "arc")
      .each((d: d3.PieArcDatum<WheelStateEntry>, i, nodes) => {
        var node = nodes[i];
        if (node) {
          node.id = "arc-" + d.data.id;
        }
      })
      .on("click", onClicked)
      .on("select", onSelected)
      .on("mouseover", function(evt, s: d3.PieArcDatum<WheelStateEntry>) {
        wheel.setState({
          hoveredId: s.data.id,
        });
        if (
          wheel.mouseEnabled &&
          loanTypes.find((x) => x.key == wheel.props.selectedType)?.id !=
          s.data.id
        ) {
          d3.select(this)
            .transition()
            .attr(
              "transform",
              function(d: d3.PieArcDatum<WheelStateEntry>) {
                let dist = 4;
                var midAngle = (d.endAngle - d.startAngle) / 2 + d.startAngle;
                let x = Math.sin(midAngle) * dist;
                let y = -Math.cos(midAngle) * dist;
                return "translate(" + x + "," + y + ") scale(1.05)";
              } as any,
            );
          wheel.props.onHoverType(s.data.name);
          // if (
          //   wheelStateData &&
          //   wheelStateData.probability >= wheel.colorThreshold
          // ) {
          //   path.style("fill", wheel.modHexLuminosity(s.data.color, 0.1));
          // } else if (wheel.state.selectedId !== s.data.id) {
          //   path.style("fill", "#9d9d9d");
          // }
        }
      })
      .on("mouseout", function(evt, s) {
        wheel.setState({
          hoveredId: undefined,
        });
        if (
          loanTypes.find((x) => x.key == wheel.props.selectedType)?.id ==
          s.data.id
        ) {
          return;
        }
        d3.select(this)
          .transition()
          .attr(
            "transform",
            function(d: any) {
              let dist = 4;
              var midAngle = (d.endAngle - d.startAngle) / 2 + d.startAngle;
              let x = Math.sin(midAngle) * dist;

              let y = -Math.cos(midAngle) * dist;
              if (d.data.id == wheel.props.selectedType) {
                return "translate(" + x + "," + y + ") scale(1.05)";
              }
              return "translate(0,0) scale(1)";
            },
          );
        wheel.props.onHoverType(undefined);
        // if (wheel.mouseEnabled) {
        //   let path = d3.select(this).select("path");
        //   var wheelStateData = wheel.state.slices[s.data.id];
        //   if (
        //     wheelStateData &&
        //     wheelStateData.probability >= wheel.colorThreshold
        //   ) {
        //     path.style("fill", s.data.color);
        //   } else if (wheel.state.selectedId !== s.data.id) {
        //     path.style("fill", "#999");
        //   }
        // }
      });

    function onClicked(evt: any, s: d3.PieArcDatum<WheelStateEntry>) {
      if (wheel.mouseEnabled && !s.data.disabled) {
        wheel.spinToSliceById(s.data.id);
        wheel.props.onSelectType(s.data.name);
        wheel.setState({
          selectedId: s.data.id,
        });
      }
    }

    function onSelected(evt: any, s: d3.PieArcDatum<WheelStateEntry>) {
      // wheel.resetSlices();
      // wheel.updateSliceColors();
      // wheel.selectSliceById(s.data.id);
    }
    g.append("div")
      .html("");
    //
    // // Draw slices
    g.append("path")
      .style("fill", function(s: any) {
        return s.data.disabled ? "#999" : s.data.color;
      })
      .style("stroke", "white")
      .style("stroke-width", `${wheel.wheelSize * 0.008}px`)
      .attr("cursor", s => s.data.disabled ? "not-allowed" : "pointer")
      .attr("s", 100)
      .transition()
      .delay(function(s, i) {
        return i * 75;
      })
      .duration(120)
      .attrTween(
        "d",
        function(d: d3.PieArcDatum<WheelStateEntry>) {
          let i = d3.interpolate(d.startAngle, d.endAngle);
          return function(t) {
            d.endAngle = i(t);
            return arc(d) || "";
          };
        },
      ).call(endall, () => {
        wheel.mouseEnabled = true;
      });

    g.filter(function(d) {
      return d.endAngle - d.startAngle > 0.1;
    })
      .append("text")
      .style("fill", "#fff")
      .attr("opacity", "0")
      .attr("transform", function(d: any, i: number) {
        let rotation = i * (360 / wheel.props.slices.length) + 291;
        return `rotate(${rotation})`;
      })
      .transition()
      .delay(800)
      .duration(400)
      .ease(d3.easeBackOut)
      .attr("opacity", "1")
      .attr("transform", function(d, i) {
        let rotation = i * (360 / wheel.props.slices.length) + 291;
        return "translate(" + arc.centroid(d) + "), rotate(" + rotation +
          ")";
      })
      .attr("cursor", d => d.data.disabled ? "not-allowed" : "pointer")
      .attr("text-anchor", "middle")
      .attr("dy", ".35em")
      .text(function(d) {
        return d.data.name;
      }).attr("font-size", "1.2em");
    //
    // // Click event helpers
    // let arcNum = 0;
    //  $(".arc").each(function () {
    //    let id = arcNum++;
    //    $(this).attr("id", `arc${id}`);
    //    $(this).attr("data-loantype", wheel.state.slices[id].name);
    //  });
    //
    //  jQuery.fn.d3Click = function () {
    //    this.each(function (i, e) {
    //      let evt = new MouseEvent("click");
    //      e.dispatchEvent(evt);
    //    });
    //  };
    //
    // jQuery.fn.d3Select = function () {
    //   this.each(function (i, e) {
    //     let evt = new Event("select");
    //      e.dispatchEvent(evt);
    //    });
    //  };
    d3.selectAll(".wheel-injection-point .arc text").each(
      function(e: any) {
        const data = e.data as WheelStateEntry;
        const glossaryEntry = wheel.loanTypeDescriptions[data.name];
        if (glossaryEntry) {
          // $(this).popover({
          //   placement: "auto",
          //   boundary: "window",
          //   animation: true,
          //   trigger: "hover focus",
          //   delay: { "show": 400, "hide": 1000 },
          //   title: glossaryEntry.Item1,
          //   content: glossaryEntry.Item2,
          //   html: true,
          // }).tooltip("hide")
          //   .tooltip("show");
        }
      },
    );

    // Animation
    function endall(transition: any, callback: any) {
      if (typeof callback !== "function") {
        throw new Error("Wrong callback in endall");
      }

      if (transition.size() === 0) callback();
      transition.on("end", function(s: any, i: any) {
        if (!i) callback(arguments);
      });
    }

    // After wheel is rendered, spin to selected slice if one exists
    if (this.props.selectedType) {
      setTimeout(() => {
        this.spinToSliceById(this.props.selectedType!);
        // Add pop out effect for selected slice
        d3.select(`.wheel-injection-point #arc-${this.props.selectedType}`)
          .transition()
          .attr(
            "transform",
            function(d: d3.PieArcDatum<WheelStateEntry>) {
              let dist = 4;
              var midAngle = (d.endAngle - d.startAngle) / 2 + d.startAngle;
              let x = Math.sin(midAngle) * dist;
              let y = -Math.cos(midAngle) * dist;
              return "translate(" + x + "," + y + ") scale(1.08)";
            } as any,
          );
      }, 100);
    }
  }

  render() {
    return (
      <div className="relative">
        <div
          className={"border z-20 transition-opacity duration-100 border-gray-300 rounded absolute top-[100%] p-2 w-full bg-white shadow-black text-black" +
            (this.state.hoveredId != undefined ? " opacity-100" : " opacity-0")}
        >
          <div>
            <b>{loanTypes.find((x) => x.key == this.state.hoveredId)?.fullName}</b>
          </div>
          {this.state.hoveredId != undefined && (
            <div
            >
              {this.props
                  .loanTypeDescriptions[
                  this.props.slices.find((x) => x.id == this.state.hoveredId)
                    ?.id || ""]}
            </div>
          )}
        </div>
        <div className="flex flex-col items-center wheel-injection-point">
        </div>
      </div>
    );
  }
}

export default Wheel;
